import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { setCurrentScreen, clearCart, setLoyaltyCode, setCurrentOrderUnpaid, createCart, addCartPayment, clearError } from '../../store/orderSlice';
import StartOverButton from '../ui/StartOverButton';
import PanarottisIcon from '../../data/PanaImages/PanarottisIcon.png';
import CardPaymentImage from '../../data/PanaImages/CardPayment.png';

const PaymentScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    isCreatingCart,
    isAddingPayment,
    isSubmittingCart,
    error: createCartError,
    payWithCardEnabled,
    payAtCounterEnabled,
  } = useSelector((state: RootState) => state.order);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const isSubmitting = isCreatingCart || isAddingPayment || isSubmittingCart;
  const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleBackToCart = () => {
    dispatch(setCurrentScreen('cart'));
  };

  const handlePayHere = async () => {
    if (!payWithCardEnabled) return;
    setPaymentError(null);
    try {
      const { cartId } = await dispatch(createCart()).unwrap();
      await dispatch(addCartPayment(cartId)).unwrap();
      dispatch(setCurrentOrderUnpaid(false));
      dispatch(setCurrentScreen('pay-here'));
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to create order');
    }
  };

  const handlePayAtCounter = async () => {
    if (!payAtCounterEnabled) return;
    setPaymentError(null);
    try {
      await dispatch(createCart()).unwrap();
      dispatch(setCurrentOrderUnpaid(true));
      dispatch(setCurrentScreen('pay-at-counter'));
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to create order');
    }
  };

  const handleStartOver = () => {
    dispatch(clearCart());
    dispatch(setLoyaltyCode(null));
    dispatch(setCurrentScreen('welcome'));
  };

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-[#f2f1ec] flex flex-col items-center justify-center px-10">
        <img
          src={PanarottisIcon}
          alt="Panarottis"
          className="w-[100px] h-[75.76px] object-contain"
        />
        <p
          className="mt-8 text-center uppercase text-[#185C53]"
          style={{
            fontFamily: nexaRustStack,
            fontSize: '48px',
            lineHeight: '52px',
            fontWeight: 900,
            letterSpacing: '2px',
          }}
        >
          Setting up your order…
        </p>
        <div className="mt-8 w-16 h-16 border-4 border-[#00A294] border-t-transparent rounded-full animate-spin" aria-hidden />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2f1ec] flex flex-col items-center px-10 pt-12">
      <div className="flex flex-col items-center w-full flex-1">
        <img
          src={PanarottisIcon}
          alt="Panarottis"
          className="w-[100px] h-[75.76px] object-contain"
        />
        <h1
          className="mt-10 text-center uppercase text-[#185C53]"
          style={{
            fontFamily: nexaRustStack,
            fontSize: '80px',
            lineHeight: '88px',
            fontWeight: 900,
            letterSpacing: '2px',
          }}
        >
          Where Would You Like to Pay?
        </h1>

        {(paymentError || createCartError) && (
          <p className="mt-4 text-center text-red-600 font-medium" style={{ fontFamily: nexaRustStack }}>
            {paymentError ?? createCartError}
          </p>
        )}

        <div className="mt-12 w-full max-w-[520px]">
          <div className="flex flex-col items-center border border-[#d5d1c9] rounded-[32px] px-10 py-16 shadow-sm">
            <img
              src={CardPaymentImage}
              alt="Pay with card"
              className="w-[360px] h-[398.71px] object-contain"
            />
            <button
              type="button"
              onClick={handlePayHere}
              disabled={isSubmitting || !payWithCardEnabled}
              className="mt-10 px-16 py-4 rounded-tr-[18px] rounded-bl-[18px] rounded-tl-none rounded-br-none uppercase shadow-[0_12px_24px_rgba(0,0,0,0.1)] disabled:opacity-100 disabled:cursor-not-allowed"
              style={{
                fontFamily: nexaRustStack,
                fontSize: '40px',
                lineHeight: '44px',
                fontWeight: 900,
                backgroundColor: payWithCardEnabled ? '#E32B26' : '#B8B8B8',
                color: payWithCardEnabled ? '#f2f1ec' : '#E6E6E6',
              }}
            >
              Pay Here
            </button>
          </div>
              </div>

        <div
          className="mt-8 text-center uppercase"
          style={{
            fontFamily: nexaRustStack,
            fontSize: '60px',
            lineHeight: '64px',
            fontWeight: 900,
            color: '#3C3C3B',
          }}
        >
          OR
              </div>

        <button
          type="button"
          onClick={handlePayAtCounter}
          disabled={isSubmitting || !payAtCounterEnabled}
          className="mt-6 px-20 py-5 rounded-tr-[18px] rounded-bl-[18px] rounded-tl-none rounded-br-none uppercase shadow-[0_12px_24px_rgba(0,0,0,0.1)] disabled:opacity-100 disabled:cursor-not-allowed"
          style={{
            fontFamily: nexaRustStack,
            fontSize: '40px',
            lineHeight: '44px',
            fontWeight: 900,
            backgroundColor: payAtCounterEnabled ? '#00A294' : '#B8B8B8',
            color: payAtCounterEnabled ? '#f2f1ec' : '#E6E6E6',
          }}
        >
          Pay at Counter
        </button>
              </div>

      <div className="w-full flex flex-col items-center gap-4 mt-auto pb-0">
        <button
          type="button"
          onClick={handleBackToCart}
          className="px-12 py-3 rounded-[18px] uppercase"
          style={{
            fontFamily: nexaRustStack,
            fontSize: '30px',
            lineHeight: '34px',
            fontWeight: 900,
            backgroundColor: 'transparent',
            color: '#00A294',
            border: '2px solid #00A294',
          }}
        >
          Back to Order
        </button>

        <StartOverButton onClick={handleStartOver} />
      </div>
    </div>
  );
};

export default PaymentScreen; 